import React, { useEffect, useRef, useState } from "react";
import "./CommentBox.css";

interface CommentBoxProps {
  pageId: string;
  className?: string;
  onCommentCountChange?: (count: number) => void;
}

declare global {
  interface Window {
    commentBox: any;
  }
}

// Get environment variables with fallbacks
const getEnvConfig = () => {
  return {
    development: {
      projectId:
        import.meta.env.VITE_COMMENTBOX_DEV_PROJECT_ID ||
        "5743655696465920-proj",
      allowedDomains: ["http://localhost:5173", "http://localhost:3000"],
    },
    production: {
      projectId:
        import.meta.env.VITE_COMMENTBOX_PROD_PROJECT_ID ||
        "5720808752152576-proj",
      allowedDomains: [
        import.meta.env.VITE_SITE_URL || "https://yourdomain.com",
      ],
    },
  };
};

const CommentBox: React.FC<CommentBoxProps> = ({
  pageId,
  className = "",
  onCommentCountChange,
}) => {
  const removeCommentBox = useRef<(() => void) | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [currentUrl, setCurrentUrl] = useState<string>("");
  const [environment, setEnvironment] = useState<"development" | "production">(
    "development"
  );
  const [config, setConfig] = useState(getEnvConfig());

  useEffect(() => {
    // Get the current URL and determine environment
    if (typeof window !== "undefined") {
      const origin = window.location.origin;
      setCurrentUrl(origin);

      // Check if we're in development or production
      // First check NODE_ENV, then fallback to URL detection
      const nodeEnv = import.meta.env.MODE;
      const isDev =
        nodeEnv === "development" ||
        (nodeEnv !== "production" &&
          (origin.includes("localhost") || origin.includes("127.0.0.1")));

      setEnvironment(isDev ? "development" : "production");
    }

    // Import the commentbox.io script
    const script = document.createElement("script");
    script.async = true;
    script.src = "https://unpkg.com/commentbox.io/dist/commentBox.min.js";
    document.body.appendChild(script);

    // Initialize commentbox when the script is loaded
    script.onload = () => {
      try {
        // Get the appropriate project ID based on environment
        const envConfig = config[environment];
        const projectId = envConfig.projectId;

        // Check if project ID has been set
        if (!projectId) {
          setError("config");
          return;
        }

        // Following the official React implementation from commentbox.io docs
        removeCommentBox.current = window.commentBox(projectId, {
          className: "commentbox",

          // Create a unique identifier for each post
          createBoxUrl: (boxId: string, pageLocation: Location) => {
            return `${pageLocation.origin}${pageLocation.pathname}?postId=${pageId}`;
          },

          // Callback when comment count changes
          onCommentCount: (count: number) => {
            if (onCommentCountChange) {
              onCommentCountChange(count);
            }
          },
        });

        console.log(
          `CommentBox initialized for post: ${pageId} in ${environment} environment`
        );
      } catch (error) {
        console.error("Error initializing commentbox:", error);

        // Check for domain error
        if (
          error instanceof Error &&
          error.message.includes("not a part of this project's domain")
        ) {
          setError("domain");
        } else {
          setError("general");
        }
      }
    };

    // Clean up function
    return () => {
      // Remove the script
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }

      // Call the cleanup function returned by commentBox
      if (removeCommentBox.current) {
        removeCommentBox.current();
      }
    };
  }, [pageId, onCommentCountChange, environment, config]);

  // Render configuration error message
  if (error === "config") {
    return (
      <div className="commentbox-container border border-orange-300 bg-orange-50 p-4 rounded-lg">
        <h3 className="text-lg font-semibold text-orange-800">
          Configuration Needed
        </h3>
        <p className="mt-2 text-orange-700">
          You need to configure your commentbox.io project IDs using environment
          variables.
        </p>
        <div className="mt-4">
          <h4 className="font-medium text-orange-800">How to fix this:</h4>
          <ol className="list-decimal ml-5 mt-2 text-orange-700 space-y-2">
            <li>
              Create a <code className="bg-orange-100 px-1 rounded">.env</code>{" "}
              file in your project root
            </li>
            <li>
              Add the following variables:
              <pre className="bg-orange-100 p-2 rounded mt-1 text-xs overflow-x-auto">
                VITE_COMMENTBOX_DEV_PROJECT_ID=your_dev_project_id
                <br />
                VITE_COMMENTBOX_PROD_PROJECT_ID=your_prod_project_id
                <br />
                VITE_SITE_URL=https://yourdomain.com
              </pre>
            </li>
            <li>Restart your development server</li>
          </ol>
        </div>
        <p className="mt-4 text-sm text-orange-600">
          Current environment: <strong>{environment}</strong>
        </p>
      </div>
    );
  }

  // Render domain error message
  if (error === "domain") {
    const envConfig = config[environment];

    return (
      <div className="commentbox-container border border-yellow-300 bg-yellow-50 p-4 rounded-lg">
        <h3 className="text-lg font-semibold text-yellow-800">
          Domain Not Registered
        </h3>
        <p className="mt-2 text-yellow-700">
          The current domain{" "}
          <code className="bg-yellow-100 px-1 rounded">{currentUrl}</code> is
          not registered in your commentbox.io project settings.
        </p>
        <div className="mt-4">
          <h4 className="font-medium text-yellow-800">How to fix this:</h4>
          <ol className="list-decimal ml-5 mt-2 text-yellow-700 space-y-2">
            <li>
              Log in to your{" "}
              <a
                href="https://commentbox.io/dashboard"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                commentbox.io dashboard
              </a>
            </li>
            <li>Select your {environment} project</li>
            <li>Go to "Settings" &gt; "Domains"</li>
            <li>
              Add{" "}
              <code className="bg-yellow-100 px-1 rounded">{currentUrl}</code>{" "}
              to the list of allowed domains
            </li>
            <li>Save your changes</li>
            <li>Refresh this page</li>
          </ol>
        </div>
        <p className="mt-4 text-sm text-yellow-600">
          Current environment: <strong>{environment}</strong>
          <br />
          Suggested domains to add:{" "}
          {envConfig.allowedDomains.map((domain) => (
            <code key={domain} className="bg-yellow-100 px-1 rounded mx-1">
              {domain}
            </code>
          ))}
        </p>
      </div>
    );
  }

  // Render general error message
  if (error === "general") {
    return (
      <div className="commentbox-container border border-red-300 bg-red-50 p-4 rounded-lg">
        <h3 className="text-lg font-semibold text-red-800">
          Error Loading Comments
        </h3>
        <p className="mt-2 text-red-700">
          There was an error initializing the comment system. Please check your
          project ID and configuration.
        </p>
        <p className="mt-4 text-sm text-red-600">
          Current environment: <strong>{environment}</strong>
          <br />
          See the browser console for more details.
        </p>
      </div>
    );
  }

  return (
    <div className={`commentbox-container ${className}`}>
      <div className="commentbox" />
      {/* Only show environment info in development mode */}
      {environment === "development" && (
        <div className="text-xs text-gray-400 mt-2">
          <p>
            Environment: <strong>{environment}</strong> | Using project ID:{" "}
            <strong>{config[environment].projectId}</strong>
          </p>
          <p>
            See{" "}
            <a
              href="https://commentbox.io/docs"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:underline"
            >
              commentbox.io documentation
            </a>{" "}
            for more details.
          </p>
        </div>
      )}
    </div>
  );
};

export default CommentBox;
